import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/*
  Public landing page (logged out). Layout + colour only.
  Google sign-in (handleGoogleLogin) and the demo flow (handleDemoLogin) are
  unchanged from the previous page. No backend/auth/data touched.
  All phone-mock / roster numbers below are SAMPLE DATA for illustration only.
*/

const C = {
  cream: "#FAF7F2",
  white: "#FFFFFF",
  border: "#E8E1D6",
  text: "#2A2623",
  muted: "#6B635C",
  teal: "#0F6B6B",
  deepTeal: "#0A4A4A",
  softBand: "#E3F0EF",
  glow: "#DCEDEB",
  terracotta: "#D9774A",
  amber: "#E9A94B",
  smallTerracotta: "#B2512A",
  darkAmber: "#8A6B1E",
};

// Live trust claims (kept from the live page).
const TRUST_CLAIMS = [
  "Built for Australian trainers",
  "GST and ABN aware invoices",
  "15 day free trial",
  "Your data, export it any time",
];

// Factual tiles, consistent with live pricing/claims.
const FACT_TILES = [
  { big: "$29", label: "a month, flat", sub: "GST included" },
  { big: "15 days", label: "free trial", sub: "No card needed" },
  { big: "1 tap", label: "from class to invoice", sub: "GST worked out for you" },
  { big: "$0", label: "per client fees", sub: "No tiers, no hidden fees" },
];

// Live pain-point copy (kept from the live page).
const PAINS = [
  {
    bar: C.teal,
    title: "Chasing payment after every session.",
    body: "You are already thinking about the next client. Instead you are texting a reminder and copying your bank details out of your notes app again.",
  },
  {
    bar: C.terracotta,
    title: "How many sessions did they have left again?",
    body: "You bought a notebook. Then a spreadsheet. Then you just guessed. They reckon they have three left. You are pretty sure it is one.",
  },
  {
    bar: C.amber,
    title: "Scrambling to reconcile at quarter end.",
    body: "Screenshots of bank transfers, missing invoice numbers, a shoebox of receipts. Your accountant emails at 9pm. There goes your weekend.",
  },
];

// Reliability roster — SAMPLE DATA only.
const ROSTER = [
  { name: "Chloe M.", pct: 96, bar: C.teal, chip: "Reliable", chipBg: "#E3F1E9", chipText: "#1D6B4A" },
  { name: "Liam C.", pct: 84, bar: "#3B7DB0", chip: "Steady", chipBg: "#E1EDF7", chipText: "#1F5C8F" },
  { name: "Ethan W.", pct: 68, bar: C.amber, chip: "Watch", chipBg: "#FBF4DD", chipText: "#8A6B1E" },
  { name: "Priya N.", pct: 48, bar: C.terracotta, chip: "At risk", chipBg: "#F9E4DF", chipText: "#A8372A" },
];

// Live comparison data (kept exactly; restyled only).
const COMPARISON_COLUMNS = [
  { key: "trainmate", label: "TrainMate", highlight: true },
  { key: "studio", label: "Studio booking software" },
  { key: "ptapps", label: "PT business apps" },
];
const COMPARISON_ROWS = [
  { feature: "Price", trainmate: "$29 AUD per month flat, GST included", studio: "Tiered plans, often from about $90 to $370+ per month, plus commissions or fees", ptapps: "Tiered plans, often from about $25 to $215+ per month, plus paid add ons" },
  { feature: "One flat plan", trainmate: "Yes", studio: "No, tiered plans", ptapps: "No, tiered plans" },
  { feature: "GST and ABN aware invoices", trainmate: "Yes", studio: "Varies", ptapps: "Varies" },
  { feature: "Invoice created on completion", trainmate: "Yes", studio: "Varies", ptapps: "Varies" },
  { feature: "Client reliability score", trainmate: "Yes", studio: "Varies", ptapps: "Varies" },
];
const COMPARISON_DISCLAIMER =
  "Competitor columns describe common tools in each category, based on public pricing pages reviewed in October 2026. Varies means it depends on the tool and plan. Prices are indicative and may change.";

// Live FAQ (kept exactly).
const FAQS = [
  { q: "Does it handle GST and BAS?", a: "Yes. When GST is switched on, your invoices show your ABN and a GST line. You also get a BAS summary PDF, plus a one tap export of your invoices and data to hand to your accountant. TrainMate does not lodge your BAS for you." },
  { q: "Do I need to install anything?", a: "No. TrainMate runs in your browser on your phone and your computer. Add it to your home screen so it opens like an app." },
  { q: "Is my client data safe?", a: "Your data is yours. We never sell it, and you can download a backup of your clients, sessions and invoices whenever you like. You can leave any time." },
  { q: "Is this actually free?", a: "You get a 15 day free trial, no card needed to start. After that it is a flat $29 AUD/month, GST included. No per client fees." },
];

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);
  const [ownGoogleAuth, setOwnGoogleAuth] = useState(false);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    api.get("/auth/config")
      .then(({ data }) => setOwnGoogleAuth(!!data?.google_own_auth_enabled))
      .catch(() => setOwnGoogleAuth(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth_error")) {
      setAuthError(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleGoogleLogin = () => {
    if (ownGoogleAuth) {
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start`;
      return;
    }
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/auth/demo-login");
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Demo account unavailable");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="lm-root" data-testid="login-page">
      <style>{STYLES}</style>

      {/* 1. NAV */}
      <nav className="lm-nav" data-testid="sticky-nav">
        <div className="lm-nav-inner">
          <a href="#top" className="lm-brand" data-testid="nav-logo" aria-label="TrainMate home">
            <span className="lm-logo" aria-hidden="true" />
            <span className="lm-brand-name">TrainMate</span>
          </a>
          <button className="lm-btn lm-btn-teal" onClick={handleGoogleLogin} data-testid="nav-start-free">
            Start free
          </button>
        </div>
      </nav>

      {/* 2. HERO */}
      <header id="top" className="lm-hero">
        <div className="lm-hero-glow" aria-hidden="true" />
        <div className="lm-wrap lm-hero-grid">
          <div className="lm-hero-left">
            <span className="lm-pill">For Australian personal trainers.</span>
            <h1 className="lm-h1">Run your PT business from your phone.</h1>
            <p className="lm-subline">Your weekly schedule, your clients and your GST invoices in one place.</p>

            {authError && (
              <div className="lm-autherror" data-testid="login-auth-error">
                We couldn&rsquo;t sign you in with Google. Please try again.
              </div>
            )}

            <div className="lm-hero-btns">
              <button className="lm-btn lm-btn-cta" onClick={handleGoogleLogin} data-testid="google-login-button">
                <GoogleIcon /> Start free trial
              </button>
              <button className="lm-btn lm-btn-outline" onClick={handleDemoLogin} disabled={demoLoading} data-testid="demo-login-button">
                {demoLoading ? "Loading demo…" : "Try the demo account"}
              </button>
            </div>
            <p className="lm-reassure">Sign in with Google. No password. No card needed to start.</p>

            <ul className="lm-chips" data-testid="trust-bar">
              {TRUST_CLAIMS.map((c) => (
                <li key={c} className="lm-chip">{c}</li>
              ))}
            </ul>
          </div>

          {/* Phone mock — SAMPLE DATA only */}
          <div className="lm-hero-right">
            <div className="lm-phone" aria-hidden="true">
              <div className="lm-phone-top">
                <span className="lm-phone-brand">TrainMate</span>
                <span className="lm-phone-avatar">D</span>
              </div>
              <div className="lm-phone-body">
                <p className="lm-phone-label">Today</p>
                <div className="lm-mini-card">
                  <div><div className="lm-mini-name">Chloe M.</div><div className="lm-mini-sub">6:00 am, 60 min</div></div>
                  <span className="lm-chip-sm" style={{ background: "#E3F1E9", color: "#1D6B4A" }}>Paid</span>
                </div>
                <div className="lm-mini-card">
                  <div><div className="lm-mini-name">Liam C.</div><div className="lm-mini-sub">7:00 am, 60 min</div></div>
                  <span className="lm-mini-btn">Complete</span>
                </div>
                <div className="lm-mini-card">
                  <div><div className="lm-mini-name">Priya N.</div><div className="lm-mini-sub">9:30 am, 45 min</div></div>
                  <span className="lm-chip-sm" style={{ background: "#EEE9E0", color: "#6B635C" }}>Scheduled</span>
                </div>
                <div className="lm-phone-tealbar">
                  <span>Invoice INV-0190 created. $90.00, GST included.</span>
                  <span className="lm-undo">Undo</span>
                </div>
              </div>
            </div>
            <div className="lm-float-card" aria-hidden="true">
              <span className="lm-red-sq" />
              <div>
                <div className="lm-float-title">Ethan W. is overdue</div>
                <div className="lm-float-sub">INV-0184, $90.00</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 3. FACT TILES */}
      <section className="lm-fact-band">
        <div className="lm-wrap">
          <h2 className="lm-h2 lm-center lm-on-teal">One flat price. No games.</h2>
          <div className="lm-fact-grid">
            {FACT_TILES.map((t) => (
              <div key={t.big} className="lm-fact-tile">
                <div className="lm-fact-big">{t.big}</div>
                <div className="lm-fact-label">{t.label}</div>
                <div className="lm-fact-sub">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section id="how-it-works" className="lm-section lm-bg-white" data-testid="section-how-it-works">
        <div className="lm-wrap">
          <p className="lm-eyebrow lm-center">How it works</p>
          <h2 className="lm-h2 lm-center">From class to invoice in a few taps.</h2>
          <div className="lm-how-grid">
            <HowCard n="1" circle={C.teal} title="Add your client." body="Name, contact and their session pack.">
              <div className="lm-visual">
                <p className="lm-visual-label">New client</p>
                <div className="lm-visual-row">Chloe M.</div>
                <div className="lm-visual-row">Price per class: $90.00 incl. GST</div>
              </div>
            </HowCard>
            <HowCard n="2" circle={C.smallTerracotta} title="Mark the class completed." body="One tap, done.">
              <div className="lm-visual">
                <div className="lm-visual-flex">
                  <div><div className="lm-mini-name">Liam C.</div><div className="lm-mini-sub">7:00 am, 60 min</div></div>
                  <span className="lm-chip-sm" style={{ background: "#E1EDF7", color: "#1F5C8F" }}>Finished</span>
                </div>
                <span className="lm-visual-cta">Complete</span>
              </div>
            </HowCard>
            <HowCard n="3" circle={C.darkAmber} title="The invoice is created for you." body="GST worked out for you.">
              <div className="lm-visual">
                <div className="lm-visual-flex">
                  <span className="lm-mini-name">INV-0190</span>
                  <span className="lm-chip-sm" style={{ background: "#E3F1E9", color: "#1D6B4A" }}>Sent</span>
                </div>
                <div className="lm-mini-sub" style={{ marginTop: 8 }}>Liam C.</div>
                <div className="lm-visual-amount">$90.00</div>
                <div className="lm-mini-sub">GST $8.18 included</div>
              </div>
            </HowCard>
          </div>
        </div>
      </section>

      {/* 5. PAIN POINTS */}
      <section id="pain-block" className="lm-section lm-bg-cream" data-testid="section-pain-block">
        <div className="lm-wrap">
          <h2 className="lm-h2">Sound familiar? You&rsquo;re not alone.</h2>
          <div className="lm-pain-grid">
            {PAINS.map((p) => (
              <div key={p.title} className="lm-pain-card">
                <span className="lm-pain-bar" style={{ background: p.bar }} aria-hidden="true" />
                <h3 className="lm-pain-title">{p.title}</h3>
                <p className="lm-pain-body">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="lm-pain-strip">The accountant&rsquo;s 9pm email? There goes your weekend.</div>
        </div>
      </section>

      {/* 6. RELIABILITY */}
      <section id="reliability" className="lm-section lm-bg-soft" data-testid="section-reliability">
        <div className="lm-wrap lm-rel-grid">
          <div>
            <p className="lm-eyebrow">Reliability</p>
            <h2 className="lm-h2">Know who&rsquo;s about to ghost. Before they do.</h2>
            <p className="lm-rel-para">
              TrainMate tracks no shows for every client over the last 90 days, so you can see who is
              worth a quick check in. Every client also keeps their sessions, invoices and packs in one place.
            </p>
          </div>
          <div className="lm-roster" data-testid="reliability-roster">
            <p className="lm-roster-head">Client roster (sample data)</p>
            {ROSTER.map((r) => (
              <div key={r.name} className="lm-roster-row">
                <span className="lm-roster-name">{r.name}</span>
                <span className="lm-roster-track"><span className="lm-roster-fill" style={{ width: `${r.pct}%`, background: r.bar }} /></span>
                <span className="lm-chip-sm" style={{ background: r.chipBg, color: r.chipText }}>{r.chip}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. COMPARISON */}
      <section id="comparison" className="lm-section lm-bg-white" data-testid="comparison-section">
        <div className="lm-wrap">
          <h2 className="lm-h2">Everything a solo trainer needs. Nothing they don&rsquo;t.</h2>
          <div className="lm-table-wrap">
            <table className="lm-table" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="lm-th">What you get</th>
                  {COMPARISON_COLUMNS.map((col) => (
                    <th key={col.key} className={col.highlight ? "lm-th lm-th-tm" : "lm-th"}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.feature}>
                    <th className="lm-td lm-td-feature" scope="row">{row.feature}</th>
                    {COMPARISON_COLUMNS.map((col) => (
                      <td key={col.key} className={col.highlight ? "lm-td lm-td-tm" : "lm-td lm-td-muted"}>{row[col.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="lm-footnote" data-testid="comparison-disclaimer">{COMPARISON_DISCLAIMER}</p>
        </div>
      </section>

      {/* 8. PRICING CTA */}
      <section id="pricing" className="lm-price-band" data-testid="section-pricing">
        <div className="lm-wrap lm-price-grid">
          <div>
            <h2 className="lm-h2">Try it free for 15 days. Then $29 a month.</h2>
            <p className="lm-price-para">
              Full access and no card needed. $29 AUD a month, flat, GST included.
              No tiers, no hidden fees, no per client fees.
            </p>
          </div>
          <div className="lm-price-cta-wrap">
            <button className="lm-btn lm-btn-dark" onClick={handleGoogleLogin} data-testid="pricing-cta">
              Start free trial
            </button>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section id="faq" className="lm-section lm-bg-white" data-testid="section-faq">
        <div className="lm-faq-wrap">
          <h2 className="lm-h2 lm-center">Questions trainers actually ask.</h2>
          <div className="lm-faq-list">
            {FAQS.map((f) => (
              <details key={f.q} className="lm-faq" data-testid="faq-item">
                <summary className="lm-faq-q">
                  <span>{f.q}</span>
                  <span className="lm-faq-plus" aria-hidden="true">+</span>
                </summary>
                <p className="lm-faq-a">{f.a}</p>
              </details>
            ))}
          </div>

          {/* Google sign-in data-use disclosure (kept for Google compliance) */}
          <div className="lm-google-note" data-testid="google-data-use">
            <h3 className="lm-google-note-title">How TrainMate uses your Google account</h3>
            <p className="lm-google-note-body">
              TrainMate is a scheduling, client and invoicing app for Australian personal trainers.
              Sign in with Google is only used to identify you. We receive your name, email address
              and profile picture, and use them only to sign you in and set up your account. We do
              not read your Gmail, Drive, Calendar or contacts. We do not sell or share this
              information, and we do not use it for advertising. TrainMate does not use Google
              services to create or process images or AI generated content. See our{" "}
              <Link to="/privacy" className="lm-inline-link" data-testid="google-data-privacy-link">Privacy Policy</Link>.
            </p>
            <p className="lm-google-note-body">
              By continuing you agree to our{" "}
              <Link to="/terms" className="lm-inline-link" data-testid="login-terms-link">Terms</Link> and{" "}
              <Link to="/privacy" className="lm-inline-link" data-testid="login-privacy-link">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer className="lm-footer">
        <div className="lm-wrap lm-footer-inner">
          <span className="lm-footer-brand">TrainMate</span>
          <nav className="lm-footer-links">
            <Link to="/privacy" className="lm-footer-link" data-testid="login-footer-privacy">Privacy</Link>
            <Link to="/terms" className="lm-footer-link" data-testid="login-footer-terms">Terms</Link>
            <Link to="/data-deletion" className="lm-footer-link" data-testid="login-footer-data-deletion">Data deletion</Link>
          </nav>
          <span className="lm-footer-copy">© 2026 TrainMate</span>
        </div>
      </footer>
    </div>
  );
}

function HowCard({ n, circle, title, body, children }) {
  return (
    <div className="lm-how-card" data-testid={`how-step-${n}`}>
      <span className="lm-how-circle" style={{ background: circle }}>{n}</span>
      <h3 className="lm-how-title">{title}</h3>
      <p className="lm-how-body">{body}</p>
      {children}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="lm-gicon" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

const STYLES = `
.lm-root{font-family:Inter,system-ui,-apple-system,sans-serif;color:#2A2623;background:#FAF7F2;-webkit-font-smoothing:antialiased;}
.lm-root *{box-sizing:border-box;}
.lm-wrap{max-width:1120px;margin:0 auto;padding:0 24px;width:100%;}
.lm-root a{color:inherit;}
.lm-root :focus-visible{outline:3px solid #0F6B6B;outline-offset:2px;border-radius:6px;}

.lm-h1{font-weight:800;letter-spacing:-0.03em;line-height:1.04;font-size:clamp(36px,8vw,60px);margin:0;}
.lm-h2{font-weight:800;letter-spacing:-0.02em;line-height:1.08;font-size:clamp(26px,5vw,38px);margin:0 0 28px;}
.lm-center{text-align:center;}
.lm-eyebrow{color:#0F6B6B;font-size:12px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;margin:0 0 10px;}

.lm-section{padding:72px 0;}
.lm-bg-white{background:#FFFFFF;}
.lm-bg-cream{background:#FAF7F2;}
.lm-bg-soft{background:#E3F0EF;}

/* Buttons */
.lm-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;min-height:44px;padding:0 20px;border-radius:12px;font-weight:700;font-size:15px;border:1px solid transparent;cursor:pointer;transition:background-color .15s,box-shadow .15s,border-color .15s,color .15s;text-decoration:none;}
.lm-btn:disabled{opacity:.6;cursor:default;}
.lm-btn-teal{background:#0F6B6B;color:#fff;}
.lm-btn-teal:hover{background:#0B5757;}
.lm-btn-cta{min-height:52px;padding:0 24px;font-size:17px;font-weight:800;color:#2A2623;background:linear-gradient(135deg,#D9774A,#E9A94B);box-shadow:0 8px 20px rgba(217,119,74,0.30);border-radius:14px;}
.lm-btn-cta:hover{box-shadow:0 10px 26px rgba(217,119,74,0.40);}
.lm-btn-outline{min-height:52px;padding:0 22px;background:#fff;border:1px solid #D6CCBE;color:#2A2623;border-radius:14px;font-weight:700;}
.lm-btn-outline:hover{border-color:#0F6B6B;color:#0F6B6B;}
.lm-btn-dark{min-height:56px;padding:0 28px;background:#2A2623;color:#fff;font-size:18px;font-weight:800;border-radius:14px;}
.lm-btn-dark:hover{background:#1c1916;}
.lm-gicon{width:20px;height:20px;}

/* Nav */
.lm-nav{position:sticky;top:0;z-index:40;background:#FAF7F2;border-bottom:1px solid #E8E1D6;}
.lm-nav-inner{max-width:1120px;margin:0 auto;padding:16px 24px;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.lm-brand{display:flex;align-items:center;gap:10px;text-decoration:none;min-height:44px;}
.lm-logo{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#0F6B6B,#0A4A4A);display:block;}
.lm-brand-name{font-size:20px;font-weight:800;letter-spacing:-0.02em;color:#2A2623;}

/* Hero */
.lm-hero{position:relative;background:#FAF7F2;padding:56px 0 72px;overflow:hidden;}
.lm-hero-glow{position:absolute;top:-120px;right:-120px;width:520px;height:520px;border-radius:50%;background:radial-gradient(circle,#DCEDEB,transparent 70%);pointer-events:none;}
.lm-hero-grid{position:relative;display:flex;flex-direction:column;gap:48px;}
.lm-hero-left{min-width:0;}
.lm-pill{display:inline-block;background:#F9E4DF;color:#A8372A;font-size:12px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;padding:6px 12px;border-radius:999px;margin-bottom:20px;}
.lm-subline{color:#6B635C;font-size:19px;line-height:1.5;max-width:520px;margin:20px 0 0;}
.lm-autherror{margin:20px 0 0;border:1px solid #ECDCA6;background:#FBF4DD;color:#7A5E18;padding:12px 14px;border-radius:12px;font-size:14px;}
.lm-hero-btns{display:flex;flex-wrap:wrap;gap:14px;margin-top:28px;}
.lm-reassure{color:#6B635C;font-size:14px;margin:14px 0 0;}
.lm-chips{list-style:none;display:flex;flex-wrap:wrap;gap:10px;padding:0;margin:28px 0 0;}
.lm-chip{background:#fff;border:1px solid #E8E1D6;color:#2A2623;font-size:13px;font-weight:600;padding:8px 14px;border-radius:999px;}

/* Phone mock */
.lm-hero-right{position:relative;display:flex;justify-content:center;padding-bottom:34px;}
.lm-phone{width:300px;max-width:84vw;border:9px solid #2A2623;border-radius:40px;background:#fff;box-shadow:0 30px 60px rgba(42,38,35,0.28);overflow:hidden;}
.lm-phone-top{background:#fff;display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #E8E1D6;}
.lm-phone-brand{font-size:15px;font-weight:800;color:#2A2623;}
.lm-phone-avatar{width:26px;height:26px;border-radius:50%;background:#0F6B6B;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;}
.lm-phone-body{background:#FAF7F2;padding:16px;}
.lm-phone-label{font-size:11px;text-transform:uppercase;letter-spacing:0.18em;color:#6B635C;font-weight:700;margin:0 0 12px;}
.lm-mini-card{background:#fff;border:1px solid #E8E1D6;border-radius:14px;padding:12px;display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;}
.lm-mini-name{font-size:14px;font-weight:700;color:#2A2623;}
.lm-mini-sub{font-size:12px;color:#6B635C;}
.lm-chip-sm{display:inline-block;font-size:12px;font-weight:700;padding:4px 10px;border-radius:999px;white-space:nowrap;}
.lm-mini-btn{background:#0F6B6B;color:#fff;font-size:12px;font-weight:700;padding:6px 12px;border-radius:10px;white-space:nowrap;}
.lm-phone-tealbar{background:#0F6B6B;color:#fff;font-size:13px;border-radius:14px;padding:12px;display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:4px;padding-right:16px;}
.lm-undo{text-decoration:underline;font-weight:700;white-space:nowrap;}
.lm-float-card{position:absolute;right:-4px;bottom:-30px;max-width:210px;background:#fff;border:1px solid #E8E1D6;border-radius:16px;box-shadow:0 16px 32px rgba(42,38,35,0.18);padding:12px 14px;display:flex;align-items:center;gap:10px;}
.lm-red-sq{width:14px;height:14px;border-radius:4px;background:#A8372A;flex:none;}
.lm-float-title{font-size:13px;font-weight:700;color:#2A2623;}
.lm-float-sub{font-size:12px;color:#6B635C;}

/* Fact band */
.lm-fact-band{background:linear-gradient(135deg,#0F6B6B,#0A4A4A);color:#fff;padding:56px 0;}
.lm-on-teal{color:#fff;}
.lm-fact-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:16px;}
.lm-fact-tile{background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.22);border-radius:20px;padding:24px;}
.lm-fact-big{font-size:44px;font-weight:800;letter-spacing:-0.02em;line-height:1;}
.lm-fact-label{font-size:17px;font-weight:700;margin-top:8px;}
.lm-fact-sub{font-size:14px;color:#D3E8E6;margin-top:6px;}

/* How it works */
.lm-how-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:20px;}
.lm-how-card{background:#FAF7F2;border:1px solid #E8E1D6;border-radius:22px;padding:24px;}
.lm-how-circle{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:18px;font-weight:800;}
.lm-how-title{font-size:20px;font-weight:800;letter-spacing:-0.01em;margin:16px 0 8px;}
.lm-how-body{font-size:16px;color:#6B635C;margin:0 0 16px;line-height:1.5;}
.lm-visual{background:#fff;border:1px solid #E8E1D6;border-radius:16px;padding:16px;}
.lm-visual-label{font-size:11px;text-transform:uppercase;letter-spacing:0.18em;color:#6B635C;font-weight:700;margin:0 0 10px;}
.lm-visual-row{background:#FAF7F2;border:1px solid #E8E1D6;border-radius:10px;padding:10px 12px;font-size:14px;color:#2A2623;margin-bottom:8px;}
.lm-visual-flex{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.lm-visual-cta{display:flex;align-items:center;justify-content:center;height:48px;background:#0F6B6B;color:#fff;font-weight:700;border-radius:12px;margin-top:12px;}
.lm-visual-amount{font-size:22px;font-weight:800;color:#2A2623;margin:2px 0;}

/* Pain */
.lm-pain-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:20px;}
.lm-pain-card{background:#fff;border:1px solid #E8E1D6;border-radius:20px;padding:24px;}
.lm-pain-bar{display:block;width:44px;height:6px;border-radius:999px;margin-bottom:16px;}
.lm-pain-title{font-size:19px;font-weight:800;letter-spacing:-0.01em;margin:0 0 10px;}
.lm-pain-body{font-size:15px;color:#6B635C;line-height:1.5;margin:0;}
.lm-pain-strip{margin-top:24px;background:linear-gradient(135deg,#D9774A,#E9A94B);color:#2A2623;border-radius:16px;padding:14px 20px;font-size:18px;font-weight:800;}

/* Reliability */
.lm-rel-grid{display:flex;flex-direction:column;gap:32px;}
.lm-rel-para{font-size:17px;color:#3F4A49;line-height:1.55;margin:0;max-width:520px;}
.lm-roster{background:#fff;border:1px solid #C9DEDC;border-radius:22px;box-shadow:0 16px 36px rgba(15,107,107,0.14);padding:20px;}
.lm-roster-head{font-size:12px;text-transform:uppercase;letter-spacing:0.14em;color:#6B635C;font-weight:700;margin:0 0 4px;}
.lm-roster-row{display:flex;align-items:center;gap:14px;border-top:1px solid #E8E1D6;padding:12px 0;}
.lm-roster-name{width:84px;flex:none;font-size:15px;font-weight:700;color:#2A2623;}
.lm-roster-track{flex:1;height:10px;background:#EEE9E0;border-radius:999px;overflow:hidden;}
.lm-roster-fill{display:block;height:100%;border-radius:999px;}

/* Comparison */
.lm-table-wrap{border:1px solid #E8E1D6;border-radius:20px;overflow:hidden;overflow-x:auto;}
.lm-table{width:100%;min-width:720px;border-collapse:collapse;font-size:15px;}
.lm-th{text-align:left;background:#FAF7F2;color:#6B635C;font-weight:700;font-size:13px;text-transform:uppercase;letter-spacing:0.08em;padding:16px 18px;border-bottom:1px solid #E8E1D6;}
.lm-th-tm{background:#0F6B6B;color:#fff;}
.lm-td{padding:16px 18px;border-bottom:1px solid #E8E1D6;vertical-align:top;text-align:left;}
.lm-td-feature{font-weight:600;color:#2A2623;}
.lm-td-muted{color:#6B635C;}
.lm-td-tm{background:#E3F0EF;color:#0A4A4A;font-weight:800;}
.lm-table tr:last-child .lm-td{border-bottom:none;}
.lm-footnote{font-size:13px;color:#6B635C;margin:16px 0 0;line-height:1.5;}

/* Pricing CTA */
.lm-price-band{background:linear-gradient(135deg,#D9774A,#E9A94B);color:#2A2623;padding:64px 0;}
.lm-price-grid{display:flex;flex-direction:column;gap:24px;}
.lm-price-para{font-size:17px;font-weight:500;line-height:1.55;margin:16px 0 0;max-width:620px;}
.lm-price-cta-wrap{display:flex;}

/* FAQ */
.lm-faq-wrap{max-width:820px;margin:0 auto;padding:0 24px;width:100%;}
.lm-faq-list{display:flex;flex-direction:column;gap:12px;}
.lm-faq{background:#FAF7F2;border:1px solid #E8E1D6;border-radius:16px;padding:0 20px;}
.lm-faq-q{display:flex;align-items:center;justify-content:space-between;gap:16px;min-height:56px;font-size:17px;font-weight:700;cursor:pointer;list-style:none;}
.lm-faq-q::-webkit-details-marker{display:none;}
.lm-faq-plus{color:#0F6B6B;font-size:24px;line-height:1;transition:transform .2s;}
.lm-faq[open] .lm-faq-plus{transform:rotate(45deg);}
.lm-faq-a{font-size:16px;color:#6B635C;line-height:1.55;margin:0 0 18px;padding-right:8px;}
.lm-google-note{margin-top:36px;padding-top:24px;border-top:1px solid #E8E1D6;}
.lm-google-note-title{font-size:14px;font-weight:700;margin:0 0 8px;color:#2A2623;}
.lm-google-note-body{font-size:13px;color:#6B635C;line-height:1.6;margin:0 0 10px;}
.lm-inline-link{color:#2A2623;text-decoration:underline;text-underline-offset:2px;}
.lm-inline-link:hover{color:#0F6B6B;}

/* Footer */
.lm-footer{background:#2A2623;color:#F3EFE8;padding:36px 0;}
.lm-footer-inner{display:flex;flex-wrap:wrap;align-items:center;gap:16px;}
.lm-footer-brand{font-size:18px;font-weight:800;}
.lm-footer-links{display:flex;flex-wrap:wrap;gap:18px;}
.lm-footer-link{color:#D9D2C6;text-decoration:none;font-size:14px;display:inline-flex;align-items:center;min-height:44px;}
.lm-footer-link:hover{color:#fff;text-decoration:underline;}
.lm-footer-copy{font-size:13px;color:#BFB7AA;margin-left:auto;}

/* Desktop two-column layouts */
@media(min-width:900px){
  .lm-hero-grid{flex-direction:row;align-items:center;}
  .lm-hero-left{flex:1;}
  .lm-hero-right{flex:0 0 340px;}
  .lm-rel-grid{flex-direction:row;align-items:center;}
  .lm-rel-grid>div:first-child{flex:1;}
  .lm-rel-grid>.lm-roster{flex:1.15;}
  .lm-price-grid{flex-direction:row;align-items:center;justify-content:space-between;}
  .lm-price-cta-wrap{flex:none;}
}
@media(max-width:420px){
  .lm-hero-btns{flex-direction:column;}
  .lm-hero-btns .lm-btn{width:100%;}
  .lm-float-card{right:0;}
}
`;
